import {TextInput as PepsicoDsTextInput} from 'pepsico-ds'
import {useRef} from 'react'
import './_textField.scss'

/**
 * TextArea component renders a text area input field with various properties.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.value=''] - The current value of the text area.
 * @param {Function} [props.updateValue=() => {}] - Function to update the value of the text area.
 * @param {string} [props.label=''] - The label for the text area.
 * @param {string} [props.error=''] - Error message to display.
 * @param {Object} [props.uiSchema={}] - UI schema object containing additional configurations.
 * @param {string} [props.uiSchema.helperText=''] - Helper text to display below the text area.
 * @param {string} [props.uiSchema.className=''] - Additional class names for styling.
 * @param {string} [props.uiSchema.placeholder=''] - Placeholder text for the text area.
 * @param {boolean} [props.uiSchema.showToolbar=false] - Whether to show the toolbar.
 * @param {boolean} [props.disabled=false] - Indicates if the text area is disabled.
 * @param {string} [props.id=''] - Optional additional id to apply to the text area.
 * @param {Object} [props.style={}] - Inline styles for the text area.
 * @param {string} [props.type='text'] - Input type.
 * @param {number} [props.min=0] - Minimum value for number inputs.
 * @param {number} [props.maxDecimals] - Maximum decimal places allowed for typed input.
 * @returns {JSX.Element} The rendered text area component.
 */
export default function TextField({
  value = '',
  updateValue = () => {},
  label = '',
  error = '',
  uiSchema = {},
  disabled = false,
  id = '',
  style = {},
  type = 'text',
  min = 0,
  maxDecimals,
}) {
  // Tracks if a minus sign is present. Cannot use e.target.value for this because
  // type="number" inputs report '' for incomplete values like '-'.
  const containsMinusRef = useRef(String(value || '').startsWith('-'))

  const sanitizeDecimalValue = incomingValue => {
    if (maxDecimals === undefined || maxDecimals === null) {
      return incomingValue
    }

    const normalizedValue = String(incomingValue).replace(',', '.')
    const [integerPart = '', ...decimalParts] = normalizedValue.split('.')
    const mergedDecimalPart = decimalParts.join('')

    if (decimalParts.length === 0) {
      return normalizedValue
    }

    const limitedDecimalPart = mergedDecimalPart.slice(0, maxDecimals)
    const sanitizedBase = `${integerPart}.${limitedDecimalPart}`

    return sanitizedBase
  }

  const getProjectedValue = (currentValue, key, selectionStart, selectionEnd) => {
    const start = Number.isInteger(selectionStart) ? selectionStart : String(currentValue).length
    const end = Number.isInteger(selectionEnd) ? selectionEnd : String(currentValue).length

    return `${String(currentValue).slice(0, start)}${key}${String(currentValue).slice(end)}`
  }

  const getNextValueAfterKey = (currentValue, key, selectionStart, selectionEnd) => {
    const rawValue = String(currentValue ?? '')
    const start = Number.isInteger(selectionStart) ? selectionStart : rawValue.length
    const end = Number.isInteger(selectionEnd) ? selectionEnd : rawValue.length

    if (key === 'Backspace') {
      if (start !== end) return `${rawValue.slice(0, start)}${rawValue.slice(end)}`
      if (start === 0) return rawValue
      return `${rawValue.slice(0, start - 1)}${rawValue.slice(start)}`
    }

    if (key === 'Delete') {
      if (start !== end) return `${rawValue.slice(0, start)}${rawValue.slice(end)}`
      if (start >= rawValue.length) return rawValue
      return `${rawValue.slice(0, start)}${rawValue.slice(start + 1)}`
    }

    if (key.length === 1) {
      return `${rawValue.slice(0, start)}${key}${rawValue.slice(end)}`
    }

    return rawValue
  }

  const shouldBlockKey = e => {
    const {key, ctrlKey, metaKey, altKey} = e
    if (ctrlKey || metaKey || altKey) return false

    // Block duplicate minus sign for number inputs.
    if (type === 'number' && key === '-') {
      const targetValue = e?.target?.value ?? value ?? ''
      return containsMinusRef.current || String(targetValue).includes('-')
    }

    // Block decimal precision exceed for number inputs with maxDecimals set
    if (maxDecimals !== undefined && maxDecimals !== null && type === 'number') {
      const isDigit = /^\d$/.test(key)
      const isSeparator = key === '.' || key === ','
      if (!isDigit && !isSeparator) return false

    const targetValue = e?.target?.value ?? value ?? ''
    const selectionStart = e?.target?.selectionStart
    const selectionEnd = e?.target?.selectionEnd
    const projectedValue = getProjectedValue(targetValue, key === ',' ? '.' : key, selectionStart, selectionEnd)
    const normalizedValue = String(projectedValue).replace(',', '.')
    const decimalPart = normalizedValue.split('.')[1]

    if (key === '.' || key === ',') {
      return normalizedValue.split('.').length > 2
    }

    return decimalPart !== undefined && decimalPart.length > maxDecimals
  }

  return false
}

  return (
    <PepsicoDsTextInput
      id={`text-input` + (id ? `_${id}` : '')}
      data-testid={`text-input` + (id ? `_${id}` : '')}
      helperText={uiSchema.helperText ?? ''}
      isDark
      label={label}
      className={`${uiSchema.className}`}
      value={value || ''}
      disabled={disabled}
      placeholderText={uiSchema.placeholder}
      onUpdate={value => {
        if (type === 'number' && value !== '') containsMinusRef.current = String(value).startsWith('-')
        updateValue(sanitizeDecimalValue(value))
      }}
      onChange={e => {
        const value = e.target.value
        if (type === 'number' && value !== '') containsMinusRef.current = value.startsWith('-')
        updateValue(sanitizeDecimalValue(value))
      }}
      onKeyDown={e => {
        if (shouldBlockKey(e)) {
          e.preventDefault()
          return
        }
        // Update minus tracking at keystroke level since e.target.value is ''
        if (type === 'number') {
          const targetValue = e?.target?.value ?? value ?? ''
          const nextValue = getNextValueAfterKey(targetValue, e.key, e?.target?.selectionStart, e?.target?.selectionEnd)
          containsMinusRef.current = String(nextValue).includes('-')
        }
      }}
      error={error}
      style={style}
      type={type}
      min={min}
    />
  )
}
